import React, { ChangeEvent, useState } from 'react';
import ITagDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagDetail';
import Chip from '@Shared.Angular/flowingly.components/Chip/Chip';
import { getService } from '@Client/runner.hooks/useService';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';

type Props = {
  tags: ITagDetail[];
  color: string;
  onAddTag: (tag: ITagDetail) => void;
  onRemoveTag: (id: string) => void;
  onRemoveAllTags: () => void;
  onUpdateTag: (arg: { name: string; updatedName: string }) => void;
  onTagNameValidationError?: (message: string) => void;
};
const MAX_TAG_NAME_LENGTH = 100;

const TagSelector: React.FC<Props> = ({
  tags,
  color,
  onAddTag,
  onRemoveTag,
  onRemoveAllTags,
  onUpdateTag,
  onTagNameValidationError
}) => {
  const [tagInput, setTagInput] = useState('');
  const tagService = getService('tagService');

  const handleTagInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTagInput(event.target.value);
    onTagNameValidationError?.('');
  };

  const isTagNameValid = (tagName: string, originalName?: string): boolean => {
    if (tagName.length > MAX_TAG_NAME_LENGTH) {
      onTagNameValidationError?.(
        `Tag name cannot exceed ${MAX_TAG_NAME_LENGTH} characters.`
      );
      return false;
    }

    if (!tagService.isValidTagName(tagName)) {
      onTagNameValidationError?.(
        'Invalid characters detected in tag name. Only letters, numbers, spaces, periods, hyphens, parentheses, and underscores are allowed.'
      );
      return false;
    }

    if (
      tags.some(
        (tag) =>
          tag.name.toLowerCase() === tagName.toLowerCase() &&
          tag.name.toLowerCase() !== (originalName?.toLowerCase() || '')
      )
    ) {
      onTagNameValidationError?.(`A Tag named '${tagName}' already exists.`);
      return false;
    }

    return true;
  };

  const handleTagKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || !tagInput.trim()) return;

    event.preventDefault();
    const newTagName = tagInput.trim();

    if (isTagNameValid(newTagName)) {
      onAddTag({ name: newTagName });
      setTagInput('');
    }
  };

  const handleUpdateTag = (args: { name: string; updatedName: string }) => {
    const { name, updatedName } = args;
    const trimmedName = updatedName.trim();

    if (!trimmedName) {
      onTagNameValidationError?.('Tag name cannot be empty.');
      return;
    }
    if (isTagNameValid(trimmedName, name)) {
      onUpdateTag({ name, updatedName: trimmedName });
    }
  };

  const clearValidationError = () => {
    onTagNameValidationError?.('');
  };

  return (
    <div className="tag-selector-outer-container">
      <div className="tag-selector-container">
        {tags.map((tag) => (
          <Chip
            key={tag.name}
            name={tag.name}
            color={color}
            onRemoveChip={onRemoveTag}
            canRemove={true}
            canEdit={true}
            onUpdateChip={handleUpdateTag}
            clearValidationError={clearValidationError}
          />
        ))}
        <div className="tag-input-wrapper">
          <i className="fa-plus"></i>
          <input
            type="text"
            value={tagInput}
            onChange={handleTagInputChange}
            onKeyDown={handleTagKeyDown}
            placeholder="Type and press Enter"
            maxLength={MAX_TAG_NAME_LENGTH}
            data-testid="tag-selector-input"
          />
        </div>
      </div>
      <div className="tag-actions">
        {tags.length > 0 && (
          <TextButton
            text="Remove All"
            type="primary-text-only"
            classNames="remove-all"
            onClick={onRemoveAllTags}
            data-testid="tag-selector-remove-all"
          />
        )}
      </div>
    </div>
  );
};

export default TagSelector;
